import Link from 'next/link';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import type { MarketingLandingCopy } from '@/i18n/marketing-landing-copy';
import { cn } from '@/lib/utils';
import { MarketingSquare } from './section-label';
import {
  getMobileNavActions,
  getMobileNavItems,
  type MobileNavItem,
} from './nav-menu-model';

/**
 * Mobile menu contents.
 *
 * Top-level solution and resource groups expand in place. Direct routes remain
 * flat rows between them so the order matches the desktop navigation.
 */
export function MobileNavPanel({
  copy,
  onNavigate,
}: {
  copy: MarketingLandingCopy;
  onNavigate: () => void;
}) {
  const items = getMobileNavItems(copy);
  const actions = getMobileNavActions(copy);

  return (
    <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto overscroll-contain">
      <div className="flex min-h-full flex-col">
        <Accordion type="single" collapsible className="shrink-0 space-y-1.5">
          {items.map((item) =>
            item.kind === 'section' ? (
              <MobileNavSection
                key={item.id}
                item={item}
                onNavigate={onNavigate}
              />
            ) : (
              <div key={item.id}>
                <Link
                  href={item.href}
                  onClick={onNavigate}
                  className="bg-marketing-surface text-marketing-fg focus-visible:outline-marketing-accent-strong flex min-h-11 items-center justify-between rounded-sm px-2.5 py-3 text-xs leading-none font-semibold outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
                >
                  {item.label}
                  <MarketingSquare size="md" />
                </Link>
              </div>
            )
          )}
        </Accordion>

        <div className="mt-auto shrink-0 pt-4 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <div className="flex flex-col gap-2">
            {actions.map((action) => (
              <Link
                key={action.id}
                href={action.href}
                onClick={onNavigate}
                className={cn(
                  'focus-visible:outline-marketing-accent-strong flex min-h-11 w-full items-center justify-between rounded-sm px-2.5 py-3 text-xs leading-none font-semibold outline-none focus-visible:outline-2 focus-visible:-outline-offset-2',
                  action.id === 'login'
                    ? 'bg-marketing-chip text-marketing-chip-fg'
                    : 'bg-marketing-cta text-marketing-cta-fg'
                )}
              >
                {action.label}
                <MarketingSquare size="md" className="bg-current" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileNavSection({
  item,
  onNavigate,
}: {
  item: Extract<MobileNavItem, { kind: 'section' }>;
  onNavigate: () => void;
}) {
  return (
    <AccordionItem value={item.id} className="border-0">
      <AccordionTrigger
        aria-label={item.accessibleTitle}
        className="group/mobile-nav bg-marketing-surface text-marketing-fg data-[state=open]:bg-marketing-panel data-[state=open]:text-marketing-panel-fg focus-visible:outline-marketing-accent-strong z-10 min-h-11 cursor-pointer items-center rounded-sm px-2.5 py-3 text-xs leading-none font-semibold outline-none hover:no-underline focus-visible:ring-0 focus-visible:outline-2 focus-visible:-outline-offset-2 [&>svg]:hidden"
      >
        <span>{item.title}</span>
        <MarketingSquare
          className="group-data-[state=open]/mobile-nav:bg-marketing-panel-fg"
          size="md"
        />
      </AccordionTrigger>
      <AccordionContent
        containerClassName="bg-marketing-accent-subtle mt-px rounded-b-md -translate-y-2 z-0"
        className="pt-12 pb-0"
      >
        <ul>
          {item.links.map((link) => (
            <li key={link.href} className="border-marketing-bg border-t">
              <Link
                href={link.href}
                onClick={onNavigate}
                className="text-marketing-accent-fg focus-visible:outline-marketing-accent-strong flex min-h-11 items-center px-2.5 py-3 text-xs leading-[1.35] font-medium outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </AccordionContent>
    </AccordionItem>
  );
}
